import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronsUpDown,
  Copy,
  Download,
  FilterX,
  RefreshCw,
  Settings2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, formatCurrency } from '@/lib/utils';
import { MetricTooltip } from './MetricTooltip';
import type { AmDashboardAccount } from '../types';

interface FilterOption {
  label: string;
  value: string;
}

interface ColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  tooltipText?: string;
  filterType?: 'text' | 'select' | 'searchable-select' | 'number-range';
  filterOptions?: FilterOption[];
  getValue: (account: AmDashboardAccount) => string | number | null;
  render?: (account: AmDashboardAccount) => ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

interface AccountPortfolioTableProps {
  accounts: AmDashboardAccount[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  sortBy: string;
  sortDirection: 1 | -1;
  onSortChange: (column: string) => void;
  onPageChange: (page: number) => void;
  columnFilters: Record<string, string>;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearColumnFilters: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  loadAllAccounts: () => Promise<AmDashboardAccount[]>;
  searchableSelectOptions?: Record<string, FilterOption[]>;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  churned: 'bg-red-100 text-red-700',
  suspended: 'bg-yellow-100 text-yellow-700',
  prospect: 'bg-blue-100 text-blue-700',
  onboarding: 'bg-purple-100 text-purple-700',
};

const VISIBILITY_STORAGE_KEY = 'atek-am-dashboard-column-visibility';

function HealthScore({ score }: { score: number | null }) {
  if (score == null) return <span className="text-muted-foreground">{'\u2014'}</span>;

  const color =
    score >= 70 ? 'text-green-600' : score >= 40 ? 'text-yellow-600' : 'text-red-600';

  return <span className={cn('font-semibold', color)}>{score}</span>;
}

function IssueBadge({
  count,
  variant,
}: {
  count: number;
  variant: 'red' | 'yellow' | 'purple';
}) {
  if (count === 0) return <span className="text-muted-foreground">{'\u2014'}</span>;

  const styles = {
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-700',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-xs font-semibold',
        styles[variant],
      )}
    >
      {count}
    </span>
  );
}

function SortHeader({
  label,
  column,
  currentSort,
  direction,
  onSort,
  align = 'left',
  tooltipText,
}: {
  label: string;
  column: string;
  currentSort: string;
  direction: 1 | -1;
  onSort: (col: string) => void;
  align?: 'left' | 'center' | 'right';
  tooltipText?: string;
}) {
  const isActive = currentSort === column;

  return (
    <div
      className={cn(
        'flex items-center gap-1',
        align === 'center' && 'justify-center',
        align === 'right' && 'justify-end',
      )}
    >
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'h-8 hover:bg-transparent',
          align === 'left' && '-ml-3',
        )}
        onClick={() => onSort(column)}
      >
        {label}
        {isActive ? (
          direction === 1
            ? <ArrowUp className="ml-1 h-3 w-3" />
            : <ArrowDown className="ml-1 h-3 w-3" />
        ) : (
          <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
        )}
      </Button>
      {tooltipText ? (
        <MetricTooltip
          label={`Explain ${label} calculation`}
          text={tooltipText}
        />
      ) : null}
    </div>
  );
}

function ContractExpiry({ days }: { days: number | null }) {
  if (days == null) return <span className="text-muted-foreground">{'\u2014'}</span>;

  if (days < 0) {
    return <span className="text-xs font-semibold text-red-600">Expired</span>;
  }

  if (days <= 90) {
    const months = Math.ceil(days / 30);
    return (
      <span className="text-xs font-semibold text-red-600">
        {months <= 1 ? `${days}d` : `${months}mo`}
      </span>
    );
  }

  const months = Math.ceil(days / 30);
  return <span className="text-xs text-muted-foreground">{months}mo</span>;
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'name',
    label: 'Account',
    sortKey: 'name',
    filterType: 'searchable-select',
    getValue: (account) => account.name,
    render: (account) => (
      <>
        <div className="font-semibold">{account.name}</div>
        <div className="text-xs text-muted-foreground">
          {account.accountNumber}
          {account.tier ? ` \u00b7 ${account.tier}` : ''}
        </div>
      </>
    ),
    className: 'min-w-[220px]',
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    filterOptions: [
      { label: 'Active', value: 'active' },
      { label: 'Churned', value: 'churned' },
      { label: 'Suspended', value: 'suspended' },
      { label: 'Prospect', value: 'prospect' },
      { label: 'Onboarding', value: 'onboarding' },
    ],
    getValue: (account) => account.status,
    render: (account) => (
      <span
        className={cn(
          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize',
          STATUS_STYLES[account.status] || 'bg-gray-100 text-gray-700',
        )}
      >
        {account.status}
      </span>
    ),
    align: 'center',
    className: 'min-w-[120px]',
  },
  {
    key: 'healthScore',
    label: 'Health',
    sortKey: 'health_score',
    filterType: 'number-range',
    getValue: (account) => account.healthScore,
    render: (account) => <HealthScore score={account.healthScore} />,
    align: 'center',
    className: 'min-w-[96px]',
  },
  {
    key: 'arr',
    label: 'ARR',
    sortKey: 'arr',
    filterType: 'number-range',
    getValue: (account) => account.arr,
    render: (account) => (account.arr != null ? formatCurrency(account.arr) : '\u2014'),
    align: 'right',
    className: 'min-w-[120px]',
  },
  {
    key: 'inactiveTransmittersCount',
    label: 'Inactive Transmitters',
    sortKey: 'inactive_transmitters',
    tooltipText:
      'Counts synced PostgreSQL transmitters for this account where activated is true but the last packet is older than 2 hours or missing, based on the latest account health snapshot.',
    filterType: 'number-range',
    getValue: (account) => account.inactiveTransmittersCount,
    render: (account) => (
      account.inactiveTransmittersCount > 0
        ? <span className="font-medium text-rose-600">{account.inactiveTransmittersCount}</span>
        : <span className="text-muted-foreground">{'\u2014'}</span>
    ),
    align: 'center',
    className: 'min-w-[156px]',
  },
  {
    key: 'sensorTotal',
    label: 'Sensors',
    tooltipText:
      'Shows active / inactive sensor buckets from live synced PostgreSQL sensor rows. Active matches the n8n KPI workflow statuses 1, 0, 8, 2, and 3. Inactive matches status 5 only. Deleted, archived, and excluded internal/test organisations are not counted.',
    filterType: 'number-range',
    getValue: (account) => account.activeSensorsCount + account.inactiveSensorsCount,
    render: (account) => (
      <>
        <span className="font-medium text-green-600">{account.activeSensorsCount}</span>
        <span className="text-muted-foreground">/</span>
        <span
          className={cn(
            'font-medium',
            account.inactiveSensorsCount > 0 ? 'text-red-500' : 'text-muted-foreground',
          )}
        >
          {account.inactiveSensorsCount}
        </span>
      </>
    ),
    align: 'center',
    className: 'min-w-[96px]',
  },
  {
    key: 'sensorsInAlarmCount',
    label: 'Alarms',
    sortKey: 'sensors_in_alarm',
    tooltipText:
      'Counts synced PostgreSQL sensors for this account where status is 2, 3, or 4, based on the latest account health snapshot.',
    filterType: 'number-range',
    getValue: (account) => account.sensorsInAlarmCount,
    render: (account) => <IssueBadge count={account.sensorsInAlarmCount} variant="red" />,
    align: 'center',
    className: 'min-w-[96px]',
  },
  {
    key: 'lowBatteryDevicesCount',
    label: 'Low Batt',
    sortKey: 'low_battery',
    tooltipText:
      'Counts synced PostgreSQL transmitters for this account where activeFlag is true, batteryPercentage is below 20, and external power is not present, based on the latest account health snapshot.',
    filterType: 'number-range',
    getValue: (account) => account.lowBatteryDevicesCount,
    render: (account) => <IssueBadge count={account.lowBatteryDevicesCount} variant="yellow" />,
    align: 'center',
    className: 'min-w-[96px]',
  },
  {
    key: 'openTicketsCount',
    label: 'Tickets',
    sortKey: 'open_tickets',
    tooltipText:
      'Counts currently open tickets linked to this account in the latest account health snapshot.',
    filterType: 'number-range',
    getValue: (account) => account.openTicketsCount,
    render: (account) => (
      account.openTicketsCount > 0
        ? <span className="font-medium">{account.openTicketsCount}</span>
        : <span className="text-muted-foreground">{'\u2014'}</span>
    ),
    align: 'center',
    className: 'min-w-[96px]',
  },
  {
    key: 'alert24hCount',
    label: 'Alerts 24h',
    sortKey: 'alert_24h_count',
    tooltipText:
      'Counts PostgreSQL alert history rows linked to this account where openedAt is within the last 24 hours, based on the latest account health snapshot.',
    filterType: 'number-range',
    getValue: (account) => account.alert24hCount,
    render: (account) => <IssueBadge count={account.alert24hCount} variant="red" />,
    align: 'center',
    className: 'min-w-[110px]',
  },
  {
    key: 'emails24hCount',
    label: 'Emails 24h',
    sortKey: 'emails_24h_count',
    tooltipText:
      'Counts PostgreSQL email history rows linked to this account where sendAt is within the last 24 hours, based on the latest account health snapshot.',
    filterType: 'number-range',
    getValue: (account) => account.emails24hCount,
    render: (account) => (
      <span className={cn(account.emails24hCount > 0 ? 'font-medium' : 'text-muted-foreground')}>
        {account.emails24hCount.toLocaleString()}
      </span>
    ),
    align: 'center',
    className: 'min-w-[110px]',
  },
  {
    key: 'calls24hCount',
    label: 'Calls 24h',
    sortKey: 'calls_24h_count',
    tooltipText:
      'Counts PostgreSQL call history rows linked to this account where createdAt is within the last 24 hours, based on the latest account health snapshot.',
    filterType: 'number-range',
    getValue: (account) => account.calls24hCount,
    render: (account) => (
      <span className={cn(account.calls24hCount > 0 ? 'font-medium' : 'text-muted-foreground')}>
        {account.calls24hCount.toLocaleString()}
      </span>
    ),
    align: 'center',
    className: 'min-w-[110px]',
  },
  {
    key: 'overdueCalibrations',
    label: 'Cal Due',
    sortKey: 'overdue_calibrations',
    tooltipText:
      'Counts calibration certificates for this account where dueDate is before today and the certificate is not locked, based on the latest account health snapshot.',
    filterType: 'number-range',
    getValue: (account) => account.overdueCalibrations,
    render: (account) => <IssueBadge count={account.overdueCalibrations} variant="purple" />,
    align: 'center',
    className: 'min-w-[96px]',
  },
  {
    key: 'daysUntilSubscriptionExpiry',
    label: 'Contract',
    sortKey: 'contract_expiry',
    tooltipText:
      'Shows the days remaining until subscription expiry from the latest account health snapshot. Accounts at 90 days or less are treated as expiring soon.',
    filterType: 'number-range',
    getValue: (account) => account.daysUntilSubscriptionExpiry,
    render: (account) => <ContractExpiry days={account.daysUntilSubscriptionExpiry} />,
    align: 'center',
    className: 'min-w-[110px]',
  },
];

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';

  let str = String(value);

  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
}

function exportToCSV(items: AmDashboardAccount[], columns: ColumnDef[], filename: string) {
  const exportColumns = columns.filter((column) => !column.noFilter);
  const header = exportColumns.map((column) => escapeCSVValue(column.label)).join(',');
  const rows = items.map((account) =>
    exportColumns.map((column) => escapeCSVValue(column.getValue(account))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: AmDashboardAccount[], columns: ColumnDef[]) {
  const exportColumns = columns.filter((column) => !column.noFilter);
  const header = exportColumns.map((column) => column.label).join('\t');
  const rows = items.map((account) =>
    exportColumns.map((column) => String(column.getValue(account) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};

  for (const column of COLUMNS) {
    defaults[column.key] = column.defaultVisible !== false;
  }

  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch {
    // Ignore invalid localStorage values.
  }

  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function renderHeaderLabel(
  column: ColumnDef,
  sortBy: string,
  sortDirection: 1 | -1,
  onSortChange: (column: string) => void,
) {
  if (column.sortKey) {
    return (
      <SortHeader
        label={column.label}
        column={column.sortKey}
        currentSort={sortBy}
        direction={sortDirection}
        onSort={onSortChange}
        align={column.align}
        tooltipText={column.tooltipText}
      />
    );
  }

  if (!column.tooltipText) {
    return column.label;
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1',
        column.align === 'center' && 'justify-center',
        column.align === 'right' && 'justify-end',
      )}
    >
      <span>{column.label}</span>
      <MetricTooltip
        label={`Explain ${column.label} calculation`}
        text={column.tooltipText}
      />
    </div>
  );
}

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: FilterOption[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try {
      return value ? JSON.parse(value) : [];
    } catch {
      return [];
    }
  }, [value]);

  const filtered = useMemo(() => {
    const base = search
      ? options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase()))
      : options;

    if (selected.length === 0) return base;

    const selectedValues = new Set(selected);
    return [...base].sort(
      (a, b) => Number(selectedValues.has(b.value)) - Number(selectedValues.has(a.value)),
    );
  }, [options, search, selected]);

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      return options.find((option) => option.value === selected[0])?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [options, selected]);

  const toggle = (optionValue: string) => {
    const next = selected.includes(optionValue)
      ? selected.filter((currentValue) => currentValue !== optionValue)
      : [...selected, optionValue];

    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full justify-between pr-6 text-xs font-normal"
          >
            <span className="truncate">{selectedLabel || '\u2014'}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {selected.length > 0 && (
          <button
            type="button"
            className="absolute right-1.5 top-1/2 z-10 -translate-y-1/2"
            onClick={() => {
              onChange(colKey, '');
              setOpen(false);
            }}
          >
            <X className="h-3 w-3 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
              No results
            </CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => toggle(option.value)}
                  className="text-xs"
                >
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      selected.includes(option.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50',
                    )}
                  >
                    {selected.includes(option.value) && <Check className="h-3 w-3" />}
                  </div>
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function AccountPortfolioTable({
  accounts,
  pagination,
  sortBy,
  sortDirection,
  onSortChange,
  onPageChange,
  columnFilters,
  onColumnFilterChange,
  onClearColumnFilters,
  onRefresh,
  isRefreshing,
  loadAllAccounts,
  searchableSelectOptions,
}: AccountPortfolioTableProps) {
  const navigate = useNavigate();
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
    loadColumnVisibility,
  );
  const [isExportingAll, setIsExportingAll] = useState(false);

  const visibleColumns = useMemo(
    () => COLUMNS.filter((column) => columnVisibility[column.key] !== false),
    [columnVisibility],
  );

  const exportColumns = useMemo(
    () => visibleColumns.filter((column) => !column.noFilter),
    [visibleColumns],
  );

  const filteredAccounts = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, value]) => value);
    if (activeFilters.length === 0) return accounts;

    return accounts.filter((account) =>
      activeFilters.every(([columnKey, filterValue]) => {
        const column = COLUMNS.find((candidate) => candidate.key === columnKey);
        if (!column || column.noFilter) return true;

        const rawValue = column.getValue(account);

        if (column.filterType === 'select' || column.filterType === 'searchable-select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue ?? ''));
          } catch {
            return true;
          }
        }

        if (column.filterType === 'number-range') {
          const [minStr = '', maxStr = ''] = filterValue.split('>>');
          const numericValue = Number(rawValue);

          if (rawValue == null || Number.isNaN(numericValue)) return false;
          if (minStr && numericValue < Number(minStr)) return false;
          if (maxStr && numericValue > Number(maxStr)) return false;
          return true;
        }

        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [accounts, columnFilters]);

  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);

  const showingText = useMemo(() => {
    if (pagination.total === 0) return 'No results';

    if (filteredAccounts.length < accounts.length) {
      return `Showing ${filteredAccounts.length} of ${accounts.length} rows on this page`;
    }

    const start = pagination.page * pagination.pageSize + 1;
    const end = Math.min((pagination.page + 1) * pagination.pageSize, pagination.total);
    return `Showing ${start}-${end} of ${pagination.total}`;
  }, [accounts.length, filteredAccounts.length, pagination.page, pagination.pageSize, pagination.total]);

  const toggleColumn = (key: string) => {
    const isCurrentlyVisible = columnVisibility[key] !== false;
    if (isCurrentlyVisible && visibleColumns.length === 1) return;

    const updated = { ...columnVisibility, [key]: !isCurrentlyVisible };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(filteredAccounts, exportColumns);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy table data');
    }
  };

  const handleExportPage = () => {
    exportToCSV(filteredAccounts, exportColumns, 'am-dashboard');
    toast.success(`Exported ${filteredAccounts.length} rows`);
  };

  const handleExportAll = async () => {
    try {
      setIsExportingAll(true);
      const allAccounts = await loadAllAccounts();
      exportToCSV(allAccounts, exportColumns, 'am-dashboard-all');
      toast.success(`Exported ${allAccounts.length} rows`);
    } catch {
      toast.error('Failed to export all data');
    } finally {
      setIsExportingAll(false);
    }
  };

  const renderColumnFilter = (column: ColumnDef) => {
    if (column.noFilter) return <TableHead key={`filter-${column.key}`} />;

    if (column.filterType === 'searchable-select') {
      const options = searchableSelectOptions?.[column.key] || [];

      return (
        <TableHead key={`filter-${column.key}`} className="p-1">
          <SearchableSelectFilter
            colKey={column.key}
            options={options}
            value={columnFilters[column.key] || ''}
            onChange={onColumnFilterChange}
          />
        </TableHead>
      );
    }

    if (column.filterType === 'select' && column.filterOptions) {
      const selected: string[] = (() => {
        try {
          return columnFilters[column.key] ? JSON.parse(columnFilters[column.key]) : [];
        } catch {
          return [];
        }
      })();

      const toggle = (optionValue: string) => {
        const next = selected.includes(optionValue)
          ? selected.filter((currentValue) => currentValue !== optionValue)
          : [...selected, optionValue];

        onColumnFilterChange(column.key, next.length ? JSON.stringify(next) : '');
      };

      return (
        <TableHead key={`filter-${column.key}`} className="p-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-full justify-start text-xs font-normal"
              >
                {selected.length ? `${selected.length} selected` : '\u2014'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {column.filterOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={selected.includes(option.value)}
                  onCheckedChange={() => toggle(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableHead>
      );
    }

    if (column.filterType === 'number-range') {
      const raw = columnFilters[column.key] || '';
      const [min = '', max = ''] = raw.split('>>');

      return (
        <TableHead key={`filter-${column.key}`} className="p-1">
          <div className="flex flex-col gap-0.5">
            <Input
              type="number"
              className="h-6 px-1 text-xs"
              value={min}
              onChange={(event) =>
                onColumnFilterChange(column.key, `${event.target.value}>>${max}`)}
              placeholder="Min"
            />
            <Input
              type="number"
              className="h-6 px-1 text-xs"
              value={max}
              onChange={(event) =>
                onColumnFilterChange(column.key, `${min}>>${event.target.value}`)}
              placeholder="Max"
            />
          </div>
        </TableHead>
      );
    }

    return (
      <TableHead key={`filter-${column.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[column.key] || ''}
          onChange={(event) => onColumnFilterChange(column.key, event.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  const renderCell = (column: ColumnDef, account: AmDashboardAccount) => {
    if (column.render) return column.render(account);

    const value = column.getValue(account);
    return value == null || value === '' ? '\u2014' : String(value);
  };

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <span className="text-sm text-muted-foreground">{showingText}</span>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {hasActiveColumnFilters && (
            <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
              <FilterX className="mr-1 h-4 w-4" />
              Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="mr-1 h-4 w-4" />
            Copy
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isExportingAll}>
                <Download className="mr-1 h-4 w-4" />
                {isExportingAll ? 'Exporting...' : 'CSV'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExportPage}>
                Export Page ({filteredAccounts.length} rows)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportAll}>
                Export All ({pagination.total})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('mr-1 h-4 w-4', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="mr-1 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.filter((column) => !column.noFilter).map((column) => {
                const checked = columnVisibility[column.key] !== false;
                const disableToggle = checked && visibleColumns.length === 1;

                return (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={checked}
                    disabled={disableToggle}
                    onCheckedChange={() => toggleColumn(column.key)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((column) => renderColumnFilter(column))}
            </TableRow>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.className,
                  )}
                >
                  {renderHeaderLabel(column, sortBy, sortDirection, onSortChange)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAccounts.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className="py-8 text-center text-muted-foreground"
                >
                  {accounts.length === 0
                    ? 'No accounts found'
                    : 'No accounts match the current column filters'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAccounts.map((account) => (
                <TableRow
                  key={account.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/accounts/${account.id}`)}
                >
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={cn(
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right',
                        column.className,
                      )}
                    >
                      {renderCell(column, account)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between border-t px-4 py-3">
        <div className="text-xs text-muted-foreground">{showingText}</div>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page === 0}
            onClick={() => onPageChange(pagination.page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.totalPages - 1}
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
